class NumberStats extends HTMLElement {
  #observer;
  #animationFrames = new Set();
  #timeouts = new Set();
  #hasAnimated = false;

  connectedCallback() {
    if (this.#hasAnimated) return;

    this.#prepareCounterWidths();

    if (
      this.dataset.animate !== 'true' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      this.#showFinalValues();
      this.#hasAnimated = true;
      return;
    }

    this.#showStartValues();

    if (!('IntersectionObserver' in window)) {
      this.#start();
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        this.#observer?.disconnect();
        this.#observer = undefined;
        this.#start();
      },
      {
        threshold: 0.25,
        rootMargin: '0px 0px -5% 0px',
      }
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = undefined;

    this.#animationFrames.forEach((frame) => cancelAnimationFrame(frame));
    this.#animationFrames.clear();

    this.#timeouts.forEach((timeout) => clearTimeout(timeout));
    this.#timeouts.clear();
  }

  #start() {
    if (this.#hasAnimated) return;
    this.#hasAnimated = true;

    const duration = Math.max(0, Number.parseInt(this.dataset.duration || '1400', 10));
    const counters = this.querySelectorAll('[data-statistics-counter]');

    counters.forEach((counter, index) => {
      if (!(counter instanceof HTMLElement)) return;

      const timeout = setTimeout(() => {
        this.#timeouts.delete(timeout);
        this.#animateCounter(counter, duration);
      }, index * 120);

      this.#timeouts.add(timeout);
    });
  }

  /**
   * @param {HTMLElement} counter
   * @param {number} duration
   */
  #animateCounter(counter, duration) {
    const target = Number.parseInt(counter.dataset.countTo || '0', 10);
    if (!Number.isFinite(target)) return;

    const start = Math.floor(target / 2);
    const difference = target - start;
    const formatter = new Intl.NumberFormat(this.dataset.locale || undefined, {
      maximumFractionDigits: 0,
    });

    counter.textContent = formatter.format(start);

    if (duration === 0 || difference === 0) {
      counter.textContent = formatter.format(target);
      return;
    }

    const startTime = performance.now();

    const update = (currentTime) => {
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      const value = Math.round(start + difference * easedProgress);

      counter.textContent = formatter.format(value);

      if (progress < 1) {
        const frame = requestAnimationFrame(update);
        this.#animationFrames.add(frame);
        return;
      }

      counter.textContent = formatter.format(target);
    };

    const frame = requestAnimationFrame(update);
    this.#animationFrames.add(frame);
  }

  #showStartValues() {
    const formatter = new Intl.NumberFormat(this.dataset.locale || undefined, {
      maximumFractionDigits: 0,
    });

    this.querySelectorAll('[data-statistics-counter]').forEach((counter) => {
      if (!(counter instanceof HTMLElement)) return;

      const target = Number.parseInt(counter.dataset.countTo || '0', 10);
      if (Number.isFinite(target)) counter.textContent = formatter.format(Math.floor(target / 2));
    });
  }

  #prepareCounterWidths() {
    const formatter = new Intl.NumberFormat(this.dataset.locale || undefined, {
      maximumFractionDigits: 0,
    });

    this.querySelectorAll('[data-statistics-placeholder]').forEach((placeholder) => {
      if (!(placeholder instanceof HTMLElement)) return;

      const target = Number.parseInt(placeholder.dataset.countTo || '0', 10);
      if (Number.isFinite(target)) placeholder.textContent = formatter.format(target);
    });
  }

  #showFinalValues() {
    const formatter = new Intl.NumberFormat(this.dataset.locale || undefined, {
      maximumFractionDigits: 0,
    });

    this.querySelectorAll('[data-statistics-counter]').forEach((counter) => {
      if (!(counter instanceof HTMLElement)) return;

      const target = Number.parseInt(counter.dataset.countTo || '0', 10);
      if (Number.isFinite(target)) counter.textContent = formatter.format(target);
    });
  }
}

if (!customElements.get('number-stats')) {
  customElements.define('number-stats', NumberStats);
}
